import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Button, EmptyState, Modal } from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import {
  useCategoriesQuery,
  useCreateCategoryMutation,
  useDeleteCategoryMutation,
  useUpdateCategoryMutation,
} from '../api';
import { CategoryForm, categoryToFormValues } from '../components/CategoryForm';
import { CategoryTable } from '../components/CategoryTable';
import type { CategoryFormValues } from '../schemas';
import { countProductsByCategory, flattenCategoryTree } from '../tree-utils';
import type { CategoryTreeRow } from '../types';

import { NextStepBanner } from '@/components/ui/NextStepBanner';
import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';
import { useToast } from '@/components/ui/toast/ToastProvider';
import { useProductsQuery } from '@/features/products/api';

/**
 * Phase 1 — the merchant Categories screen this app never had (`nav.ts`
 * marked it "Bientôt" while the backend, `CategoryController`
 * (Sprint 7, Lot 1), has offered full CRUD for months). No client-side
 * role gate: `@RequirePermission('catalog:write')` is the same single
 * guard `ProductListPage` already answers to, not the
 * `merchant_owner`-only posture Promotions takes — a real 403 from the
 * API is shown exactly like any other query error, never pre-guessed.
 */
export function CategoryListPage(): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const categoriesQuery = useCategoriesQuery();
  const productsQuery = useProductsQuery();
  const createMutation = useCreateCategoryMutation();
  const updateMutation = useUpdateCategoryMutation();
  const deleteMutation = useDeleteCategoryMutation();

  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [editingRow, setEditingRow] = useState<CategoryTreeRow | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showNextStep, setShowNextStep] = useState(false);

  function openCreate(): void {
    setFormError(null);
    setEditingRow(null);
    setModalMode('create');
  }

  function openEdit(row: CategoryTreeRow): void {
    setFormError(null);
    setEditingRow(row);
    setModalMode('edit');
  }

  function closeModal(): void {
    setModalMode(null);
    setEditingRow(null);
  }

  async function onSubmit(values: CategoryFormValues): Promise<void> {
    setFormError(null);
    // Captured before the mutation, the same reasoning as StorePage's own
    // `isFirstSave`: once the create succeeds, the categories query refetches
    // and this shop no longer has zero categories.
    const isFirstCategory = (categoriesQuery.data ?? []).length === 0;
    try {
      if (modalMode === 'edit' && editingRow !== null) {
        await updateMutation.mutateAsync({ categoryId: editingRow.category.id, values });
        toast.success(t('merchant.categories.updated'));
      } else {
        await createMutation.mutateAsync(values);
        toast.success(t('merchant.categories.created'));
        if (isFirstCategory) setShowNextStep(true);
      }
      closeModal();
    } catch (error) {
      setFormError(t(resolveErrorMessageKey(error)));
    }
  }

  async function onDelete(row: CategoryTreeRow): Promise<void> {
    const warning =
      row.productCount > 0
        ? t('merchant.categories.deleteWarning', { count: row.productCount })
        : '';
    if (
      !window.confirm(t('merchant.categories.deleteConfirm', { name: row.category.name, warning }))
    ) {
      return;
    }

    setDeletingId(row.category.id);
    try {
      await deleteMutation.mutateAsync(row.category.id);
      toast.success(t('merchant.categories.deleted'));
    } catch (error) {
      toast.error(t(resolveErrorMessageKey(error)));
    } finally {
      setDeletingId(null);
    }
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const isPending = categoriesQuery.isPending || productsQuery.isPending;
  const categories = categoriesQuery.data ?? [];
  const products = productsQuery.data ?? [];

  const productCounts = countProductsByCategory(products);
  const rows = flattenCategoryTree(categories, productCounts);
  const parentNameById = new Map(categories.map((category) => [category.id, category.name]));

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">
            {t('merchant.nav.categories')}
          </h1>
          <p className="text-sm text-inkFaint">{t('merchant.categories.subtitle')}</p>
        </div>
        <Button onClick={openCreate}>{t('merchant.categories.newCategory')}</Button>
      </div>

      {showNextStep ? (
        <NextStepBanner
          title={t('merchant.categories.nextStep.title')}
          description={t('merchant.categories.nextStep.description')}
          ctaLabel={t('merchant.categories.nextStep.cta')}
          to="/products/new"
          onDismiss={() => {
            setShowNextStep(false);
          }}
        />
      ) : null}

      {isPending ? (
        <TableSkeleton columns={6} />
      ) : categoriesQuery.isError ? (
        <EmptyState
          title={
            categoriesQuery.error.status === 403
              ? t('merchant.common.accessDenied')
              : t('merchant.categories.loadError')
          }
          description={
            categoriesQuery.error.status === 403
              ? t('merchant.categories.accessDeniedDescription')
              : t(resolveErrorMessageKey(categoriesQuery.error))
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('merchant.categories.emptyTitle')}
          description={t('merchant.categories.emptyDescription')}
          action={<Button onClick={openCreate}>{t('merchant.categories.newCategory')}</Button>}
        />
      ) : (
        <CategoryTable
          rows={rows}
          parentNameById={parentNameById}
          onEdit={openEdit}
          onDelete={(row) => {
            void onDelete(row);
          }}
          deletingId={deletingId}
        />
      )}

      <Modal
        open={modalMode !== null}
        title={
          modalMode === 'edit'
            ? t('merchant.categories.editTitle')
            : t('merchant.categories.newCategory')
        }
        onClose={closeModal}
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>
              {t('merchant.common.cancel')}
            </Button>
            <Button type="submit" form="category-form" loading={isSubmitting}>
              {modalMode === 'edit'
                ? t('merchant.common.save')
                : t('merchant.categories.createAction')}
            </Button>
          </>
        }
      >
        {formError !== null ? (
          <p
            role="alert"
            className="mb-3 rounded-md bg-criticalSoft px-3 py-2 text-sm text-critical"
          >
            {formError}
          </p>
        ) : null}
        <CategoryForm
          formId="category-form"
          categories={categories}
          {...(editingRow !== null ? { editingCategoryId: editingRow.category.id } : {})}
          {...(editingRow !== null
            ? { defaultValues: categoryToFormValues(editingRow.category) }
            : {})}
          onSubmit={(values) => {
            void onSubmit(values);
          }}
          isSubmitting={isSubmitting}
        />
      </Modal>
    </div>
  );
}
